import React from 'react';
import Image from 'next/image';

// Small presentational component for a feature item
const Feature = ({ icon, title, desc }) => (
  <div className="flex flex-col items-center text-center gap-3">
    <div className="w-12 h-12 flex items-center justify-center text-[#112140]">
      {icon}
    </div>
    <h3 className="text-[16px] sm:text-[17px] font-semibold text-[#112140]">{title}</h3>
    <p className="text-[14px] leading-6 text-[#475467] max-w-[280px]">
      {desc}
    </p>
  </div>
);

const WorkAndInsightsHero = () => {
  return (
    <section className="w-full bg-white">
      {/* Top hero: two-column layout */}
      <div className="max-w-[1300px] mx-auto px-4 lg:px-0 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          {/* Copy */}
          <div>
            <h1 className="text-[#112140] text-[32px] sm:text-[40px] md:text-[48px] font-bold leading-tight">
              Bringing Brands to Life,{' '}
              <br className="hidden sm:block" />
              One Label at a Time
            </h1>
            <p className="mt-4 text-[#475467] text-[16px] sm:text-[18px] max-w-[600px]">
              At Royal Prime Labels, every label is more than a design — it’s a story, a promise, and a shelf presence. With in-house expertise, advanced technology, and a sustainability-first mindset, we help brands create packaging that inspires trust and drives impact.
            </p>
          </div>
          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-[0_6px_16px_0_rgba(0,0,0,0.08)]">
              <Image
                src="/images/warehouse-labels-portfolio.png"
                alt="Royal Prime Labels Warehouse - shelves stacked with rolls of premium self-adhesive packaging labels"
                width={780}
                height={520}
                className="w-full h-auto object-cover"
                priority
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom feature band */}
      <div className="w-full bg-[#E8ECF1]">
        <div className="max-w-[1300px] mx-auto px-4 lg:px-0 py-10 md:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-10">
            <Feature
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 7h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7Z" stroke="#1A5AFF" strokeWidth="1.5" />
                  <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" stroke="#1A5AFF" strokeWidth="1.5" />
                  <circle cx="8" cy="14.5" r="1" fill="#1A5AFF" />
                  <circle cx="12" cy="14.5" r="1" fill="#1A5AFF" />
                  <circle cx="16" cy="14.5" r="1" fill="#1A5AFF" />
                </svg>
              }
              title="In-House Expertise"
              desc="End-to-end control of production under one roof for speed, consistency, and quality."
            />
            <Feature
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3v4m8-4v4M4 9h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9Z" stroke="#1A5AFF" strokeWidth="1.5" />
                  <path d="M9 14h6M9 17h6" stroke="#1A5AFF" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              }
              title="Custom Solutions"
              desc="Tailored label designs and technical consultation to meet every brand’s unique needs."
            />
            <Feature
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 21V9l4-3 4 3 4-3 4 3v12" stroke="#1A5AFF" strokeWidth="1.5" />
                  <path d="M8 21V9M12 21V9M16 21V9" stroke="#1A5AFF" strokeWidth="1.5" />
                </svg>
              }
              title="Delighted Customers"
              desc="Trusted by leading brands across 10+ industries for reliable and impactful results."
            />
            <Feature
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="6" width="18" height="12" rx="2" stroke="#1A5AFF" strokeWidth="1.5" />
                  <path d="M7 10h10M7 14h6" stroke="#1A5AFF" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              }
              title="Technology Leadership"
              desc="Advanced flexographic, digital, and specialty finishes that set new standards in print."
            />
            <Feature
              icon={
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 13c-1.657 0-3 1.343-3 3v2h6v-2c0-1.657-1.343-3-3-3ZM17 13c-1.657 0-3 1.343-3 3v2h6v-2c0-1.657-1.343-3-3-3Z" stroke="#1A5AFF" strokeWidth="1.5" />
                  <path d="M7 11a4 4 0 0 0 4-4V5a3 3 0 1 0-6 0v2a4 4 0 0 0 2 3.465M17 11a4 4 0 0 0 4-4V5a3 3 0 1 0-6 0v2a4 4 0 0 0 2 3.465" stroke="#1A5AFF" strokeWidth="1.5" />
                </svg>
              }
              title="Sustainability First"
              desc="Eco-friendly materials and certified processes that align with modern brand values."
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkAndInsightsHero;
